import { CSSProperties } from "react";
import { Link, createSearchParams } from "react-router-dom";
import { saveAs } from "file-saver";
import { Tooltip, IconButton } from "@mui/material";
import { Info } from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ColDef, GridApi, GetContextMenuItemsParams, MenuItemDef, IServerSideSelectionState } from "ag-grid-enterprise";
import { DatesFormat } from "./DateFormats";
import { formatDateTo, genericGridDateFiltering, genericGridDateFilteringRestApi, utcToLocalDateTime } from "./DateTimeUtils";
import { BO_CORE_DEFAULT_NUMBER_VALUE, BO_CORE_DEFAULT_VALUE, DEFAULT_BOCORE_DATE, toastError, toBool } from "./AppUtils";
import { getRequisitionPDF, getTestResultPDF, getZipArchiveForOrder } from "../config/axios/apis/order/order";
import { isHttpErrorMessageAvailable } from "../config/axios/Axios";
import { ColumnHeaderCopyWrapper } from "../components/GenericProGrid/components/ColumnHeaderCopyWrapper/ColumnHeaderCopyWrapper";
import CopyToClipboard from "../components/Common/CopyToClipboard/CopyToClipboard";
import GridLinkCell from "../components/GenericProGrid/components/GridLinkCell/GridLinkCell";

export type CustomTypeColumnDefinition = {
    attrKey: string;
    atrbType: "text" | "boolean" | "date" | "timestamp" | "json" | "number" | "lookup" | "value list";
    name: string;
};

const filterMapping = {
    contains: "containsIgnoreCase",
    notContains: "notContainsIgnoreCase",
    equals: "equal",
    notEqual: "notEqual",
    startsWith: "startsWith",
    endsWith: "endsWith",
    lessThan: "lessThan",
    lessThanOrEqual: "lessThanOrEqual",
    greaterThan: "greaterThan",
    greaterThanOrEqual: "greaterThanOrEqual",
    inRange: "in",
};

const filterMappingRestApi = {
    contains: "=iin=",
    notContains: "=iout=",
    equals: "==",
    notEqual: "!=",
    startsWith: "=isw=",
    endsWith: "=iew=",
    lessThan: "=lt=",
    lessThanOrEqual: "=le=",
    greaterThan: "=gt=",
    greaterThanOrEqual: "=ge=",
    inRange: "=in=",
};

// Equal: ==
// Not equal: !=
// Greater than: =gt=
// Greater than or equal: =ge=
// Less than: =lt=
// Less than or equal: =le=
// In: =in=
// In ignore case: =iin=
// Not in: =out=
// Not in ignore case: =iout=
// Like regex: =re=
// Not like regex: =nre=

// Starts with: =sw=
// Starts with ignore case: =isw=
// Ends with: =ew=
// Ends with ignore case: =iew=
// Contains: =c=
// Contains ignore case: =ic=
// Not contains: =nc=
// Not contains ignore case: =inc=
// Equal ignore case: =i=
// Not equal ignore case: =ni=
// Has key: =hk=
// Has any key: =hak=
// Has value: =hv=
// Has any value: =hav=

export const DEFAULT_COLID_DOTS = "#";
export const DEFAULT_COLID_AT = "@";

const valueConversion = (value) => {
    if (value === null || value === undefined) {
        return value;
    }

    if (typeof value === "string") {
        try {
            const notSecureCharacters = ["=", "+", "@", ",", "-"];
            const needsModification = notSecureCharacters.some((item) => value.startsWith(item));
            if (needsModification) {
                return `'${value}`;
            }
        } catch {
            return value;
        }
    }

    return value;
};

const getAllDisplayedRows = (gridApi: GridApi) => {
    const displayedRowsData: any[] = [];
    // node.data is undefined when the row is in loading state. That is causing the errors.
    gridApi.forEachNode((node) => node.data && displayedRowsData.push(node.data));

    return displayedRowsData;
};

export const getSelectedRowsCustom = (gridApi: GridApi | null) => {
    if (gridApi) {
        if (gridApi.getGridOption("rowModelType") === "serverSide") {
            const isAllRowsSelected = gridApi.getServerSideSelectionState() as IServerSideSelectionState;

            if (isAllRowsSelected?.selectAll) {
                const displayedRows = getAllDisplayedRows(gridApi);
                if (isAllRowsSelected.toggledNodes.length) {
                    const filteredNodes = displayedRows.filter((item) => !isAllRowsSelected.toggledNodes.includes(item.id.toString()));
                    return filteredNodes;
                }
                return displayedRows;
            }
        }
        return gridApi.getSelectedRows();
    }
    return [];
};

export const exportSelectedOnly = (gridApi: GridApi | null): boolean => {
    if (!gridApi) return false;

    const selectedRowsCount = getSelectedRowsCustom(gridApi).length;

    if (gridApi.getGridOption("rowModelType") === "serverSide") {
        const isSelectAllState = gridApi.getServerSideSelectionState() as IServerSideSelectionState;
        return !isSelectAllState?.selectAll && selectedRowsCount > 0;
    }

    return selectedRowsCount > 0;
};

export const defaultCellProcessor = (params) => {
    const colId = params.column.getColId();
    return colId === DEFAULT_COLID_DOTS ? null : valueConversion(params.value);
};

export const defaultHeaderProcessor = (params) => {
    const colId = params.column.getColId();
    const { headerName } = params.column.getColDef();

    return colId === DEFAULT_COLID_DOTS ? null : headerName;
};

export const renderLocalDateTime = (value, hasTime = false) => {
    if (value && typeof value !== "undefined") {
        if (value === DEFAULT_BOCORE_DATE) {
            return (
                <span>
                    <CopyToClipboard>n/a</CopyToClipboard>
                </span>
            );
        }
        return (
            <Tooltip title={formatDateTo(value, DatesFormat.defaultUSDateTimeFormat)}>
                <span>
                    <CopyToClipboard>{formatDateTo(value, hasTime ? DatesFormat.defaultUSDateTimeFormat : DatesFormat.defaultUSFormat)}</CopyToClipboard>
                </span>
            </Tooltip>
        );
    }
    return (
        <span>
            <CopyToClipboard>n/a</CopyToClipboard>
        </span>
    );
};

// RSQL requires string values to be quoted (e.g. roles == 'System Admin'); booleans stay bare.
const formatRsqlValue = (val: string): string => (val === "true" || val === "false" ? val : `'${val.replace(/'/g, "\\'")}'`);

const normalizeYesNo = (val: string): string => {
    if (val === "Yes") return "true";
    if (val === "No") return "false";
    return val;
};

const buildValuesClause = (key: string, values: string[], connector: string): string => {
    const arrayString = values.map(normalizeYesNo);
    if (arrayString.length === 0) {
        return `${key} == '' ${connector}`;
    }
    if (arrayString.length === 1) {
        return `${key} == ${formatRsqlValue(arrayString[0])} ${connector}`;
    }
    // Multi-value OR group, wrapped in parens, with the outer-column connector appended after the closing paren.
    // Connector check uses the outer `index` (column position) — not arrayIndex,
    // which is the position within the values array. Mixing them dropped the "and "
    // after the closing paren when more filter columns followed (e.g. status OR + roles).
    const inner = arrayString.map((v) => `${key} == ${formatRsqlValue(v)}`).join(" or ");
    return `(${inner}) ${connector}`;
};

const buildNumberClause = (key: string, filter: any, filterName: string, connector: string): string => {
    if (filter?.type === "inRange") {
        return `${key} =gt= ${filter.filter} and =lt= ${filter.filterTo} ${connector}`;
    }
    if (filter?.type === "blank") {
        return `${key} == -1 ${connector}`;
    }
    if (filter?.type === "notBlank") {
        return `${key} != -1 ${connector}`;
    }
    return `${key} ${filterName} ${filter?.filter} ${connector}`;
};

const buildTextClause = (key: string, filter: any, filterName: string, connector: string): string => {
    if (filter?.type === "blank") {
        return `${key} == '' ${connector}`;
    }
    if (filter?.type === "notBlank") {
        return `${key} != '' ${connector}`;
    }
    const escapedFilter = filter?.filter?.replace(/'/g, "\\'");
    return `${key} ${filterName} '${escapedFilter}' ${connector}`;
};

export const generateRestApiFilterString = (filterModel: any) => {
    let filterString = "";
    const { restApiFts, ...rest } = filterModel;
    const keys: string[] = Object.keys(rest);

    keys.forEach((key, index) => {
        const filter = filterModel[key];
        const filterName = filterMappingRestApi[filter?.type];
        const connector = index >= keys.length - 1 ? "" : "and ";

        if (filter?.values) {
            filterString += buildValuesClause(key, filter.values, connector);
        } else if (filter?.filterType === "number") {
            filterString += buildNumberClause(key, filter, filterName, connector);
        } else if (filter?.filterType === "date") {
            filterString += genericGridDateFilteringRestApi(filterModel, key, filterName, index, keys);
        } else if (filter?.filterType === "text") {
            filterString += buildTextClause(key, filter, filterName, connector);
        } else {
            filterString += `${key}:{${filterName}:"${filter?.filter}"},`;
        }
    });
    return { filterString, restApiFts };
};

export const generateRestApiSortString = (sortModel: any) => {
    const sortingString: any = [];
    sortModel?.forEach((sortObj): any => {
        sortingString.push(`${sortObj?.colId};${sortObj?.sort?.toLowerCase()}`);
    });
    return sortingString;
};

export const generateGqlFilterObject = (filterModel: any): object => {
    let filterObject: Record<string, any> = {};
    Object.keys(filterModel).forEach((property) => {
        const filterName = filterMapping[filterModel[property]?.type];
        if (property === "-") {
            filterObject.searchText = filterModel["-"]?.filter;
        } else if (filterModel[property]?.values) {
            const arrayString = filterModel[property]?.values.map((val) => {
                if (val === "Yes") return true;
                if (val === "No") return false;
                return val;
            });

            filterObject[property] = { in: arrayString };
        } else if (filterModel[property]?.filterType === "number") {
            if (filterModel[property]?.type === "inRange") {
                const fromNumber = filterModel[property].filter;
                const toNumber = filterModel[property].filterTo;
                filterObject[property] = { greaterThan: fromNumber, lessThan: toNumber };
            } else if (filterModel[property]?.type === "blank") {
                filterObject[property] = { equal: -1 };
            } else if (filterModel[property]?.type === "notBlank") {
                filterObject[property] = { notEqual: -1 };
            } else {
                filterObject[property] = { [filterName]: filterModel[property]?.filter };
            }
        } else if (filterModel[property]?.filterType === "date") {
            filterObject = { ...filterObject, ...genericGridDateFiltering(filterModel, property, filterName) };
        } else if (filterModel[property]?.filterType === "text") {
            if (filterModel[property]?.type === "blank") {
                filterObject[property] = { equal: "" };
            } else if (filterModel[property]?.type === "notBlank") {
                filterObject[property] = { notEqual: "" };
            } else {
                filterObject[property] = { [filterName]: filterModel[property]?.filter };
            }
        } else {
            filterObject[property] = { [filterName]: filterModel[property]?.filter };
        }
    });

    return filterObject;
};

export const generateGqlSort = (sortModel: any[]): any[] => {
    return sortModel?.map((sortObj) => {
        return {
            attr: sortObj?.colId,
            order: sortObj?.sort?.toUpperCase(),
        };
    });
};

export const booleanCellRenderer = (value) => {
    if (toBool(value)) {
        return (
            <span>
                <FormattedMessage id="common.button-label.yes" defaultMessage="Yes" />{" "}
            </span>
        );
    }

    return (
        <span>
            <FormattedMessage id="common.button-label.no" defaultMessage="No" />{" "}
        </span>
    );
};

/**
 * Hyperlink color for AG Grid cell renderers.
 *
 * Returns the `--oneh-grid-link` CSS variable rather than a resolved hex: AG Grid does not re-run
 * cell renderers when the MUI theme changes, so a hex captured at render time would keep the
 * previous color mode until the grid refreshed. A CSS variable re-resolves at paint time and flips
 * with <html data-color-mode> on its own. The variable is defined for both modes in src/index.css.
 */
export const getGridLinkColor = (): string => "var(--oneh-grid-link)";

// Grid "link" cells that trigger JS (no navigable href) render as native buttons: keyboard-focusable
// and exposed to assistive tech without ARIA workarounds (SonarQube S6819). The reset keeps the
// button visually identical to the plain link text it replaces. A function (not a const) so the
// link color is resolved per render from the active color mode.
const gridLinkButtonStyle = (): CSSProperties => ({
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    textAlign: "left",
    color: getGridLinkColor(),
    cursor: "pointer",
});

export const additionalLinkColumnRenderer = (value: any, rowData: any, properties: any, navigate: any) => {
    if (value && (typeof value === "number" || typeof value === "string")) {
        if (properties?.target === "_blank") {
            if (typeof properties?.link === "function") {
                return (
                    <button type="button" style={gridLinkButtonStyle()} onClick={() => properties.link(rowData)}>
                        {value}
                    </button>
                );
            }
            return (
                <Link
                    style={{ color: getGridLinkColor(), textDecoration: "none" }}
                    target="_blank"
                    to={{
                        hash: "#hash",
                        pathname: `${properties?.link}/${rowData.id}`,
                        search: `?${createSearchParams(properties?.queryParams)}`,
                    }}
                >
                    {value}
                </Link>
            );
        }
        return (
            <button
                type="button"
                style={gridLinkButtonStyle()}
                onClick={() => {
                    if (typeof properties?.link === "function") {
                        properties.link(rowData);
                    } else {
                        navigate({
                            pathname: `${properties?.link}/${rowData.id}`,
                            search: `?${createSearchParams(properties?.queryParams)}`,
                        });
                    }
                }}
            >
                {value}
            </button>
        );
    }
    return <span />;
};

export const renderDots = (_value, params) => {
    return (
        <div style={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <MoreVertIcon
                onClick={(_e) => {
                    if (getSelectedRowsCustom(params.api).length === 0) {
                        params.node?.setSelected(true);
                    }

                    if (getSelectedRowsCustom(params.api).length === 1 && !params.node?.isSelected()) {
                        params.api.deselectAll();
                        params.node?.setSelected(true);
                    }

                    // Alternative to params.api.menuService.contextMenuFactory.showMenu (version 31.1)
                    // const rmbClickEvent = new PointerEvent("contextmenu", {
                    //     bubbles: e.nativeEvent.bubbles,
                    //     cancelable: e.nativeEvent.cancelable,
                    //     view: e.nativeEvent.view,
                    //     button: 2,
                    //     buttons: 0,
                    //     clientX: e.nativeEvent.clientX,
                    //     clientY: e.nativeEvent.clientY,
                    // });
                    // e.nativeEvent.target?.dispatchEvent(rmbClickEvent);

                    params.api.showContextMenu({ x: null, y: null, rowNode: params.node, column: params.column, value: params.data }); // Working on version 32.0, https://www.ag-grid.com/react-data-grid/context-menu/
                    // params.api.contextMenuFactory.showMenu(params.node, params.column, params.data, e?.nativeEvent); // Not working from version 31.1
                    // params.api.menuService.contextMenuFactory.showMenu(params.node, params.column, params.data, e?.nativeEvent); // Working on version 31.1, but not working on version 32.0
                }}
                style={{ cursor: "pointer" }}
            />
        </div>
    );
};

/**
 * Resolves the full-page destination of an ID cell, or null when the row has none (NG-8661).
 *
 * A non-empty string `editLink` is the plain "<base>/<id>" case. Everything else has no page to
 * link to: a function `editLink` opens a fly-in, and several grids pass the empty string to mean
 * "the ID opens nothing" — that must not become an `<a href="/42">`.
 */
const resolveIdColumnDestination = (value, editLink, editLinkQueryParams): string | null => {
    if (typeof editLink !== "string" || !editLink) {
        return null;
    }
    const search = createSearchParams(editLinkQueryParams).toString();
    const query = search ? `?${search}` : "";
    return `${editLink}/${value}${query}`;
};

export const idColumnCellRenderer = (value, params, editLinkNewTab, editLink, editLinkQueryParams, navigate) => {
    if (value && typeof value === "number") {
        const newTabDestination = editLinkNewTab ? resolveIdColumnDestination(value, editLink, editLinkQueryParams) : null;
        if (newTabDestination) {
            // NG-8661: this is the one branch that literally opens a new tab, so it needs `?ctx`
            // more than any other — without it the tab boots into the default organization, which
            // is the whole defect this ticket fixes. It went through a bare RouterLink before.
            return (
                <CopyToClipboard>
                    <GridLinkCell to={newTabDestination} newTab>
                        {value}
                    </GridLinkCell>
                </CopyToClipboard>
            );
        }
        // A row that opens a full page renders as an anchor (NG-8661): middle click / Cmd-click
        // open it in a new tab in the current organization, right-click offers "Open link in new
        // tab", and left click still routes client-side. Rows that open a fly-in have no
        // destination and stay a button.
        //
        // GridLinkCell rather than a hand-rolled <a>: it resolves the tenant that `?ctx` carries
        // through the same `useAppLink` path every other link in the app uses. Building the props
        // here instead would read the tenant from sessionStorage while the rest of the app reads
        // it from Redux — one destination, two sources of truth.
        const destination = resolveIdColumnDestination(value, editLink, editLinkQueryParams);
        if (destination) {
            return (
                <CopyToClipboard>
                    <GridLinkCell to={destination}>{value}</GridLinkCell>
                </CopyToClipboard>
            );
        }
        return (
            <CopyToClipboard>
                <button
                    type="button"
                    style={gridLinkButtonStyle()}
                    onClick={() => {
                        if (typeof editLink === "function") {
                            editLink(params.data);
                        } else {
                            navigate({ pathname: `${editLink}/${value}`, search: `?${createSearchParams(editLinkQueryParams)}` });
                        }
                    }}
                >
                    {value}
                </button>
            </CopyToClipboard>
        );
    }
    return <span>n/a</span>;
};

// export const buttonCellRenderer = (value: any, lastColumnButtonFunc: any, lastColumnButtonLabel: string | undefined) => {
//     return (
//         <button type="button" onClick={() => lastColumnButtonFunc(value)}>
//             <span>{lastColumnButtonLabel}</span>
//         </button>
//     );
// };

export const defaultCellRenderer = (value) => {
    if (Array.isArray(value)) {
        const textArray = value?.map((obj) => {
            if (typeof obj === "string") {
                return <span key={obj}>{obj}</span>;
            }
            return <span key={`n/a:${obj.toString()}`}>n/a</span>;
        });
        return (
            <div>
                <CopyToClipboard>{textArray}</CopyToClipboard>
            </div>
        );
    }
    if (typeof value === "object") {
        if (value?.value) {
            return (
                <CopyToClipboard>
                    <span>{value?.value}</span>
                </CopyToClipboard>
            );
        }
        return (
            <CopyToClipboard>
                <span>n/a</span>
            </CopyToClipboard>
        );
    }
    if ((value || value === 0) && value !== BO_CORE_DEFAULT_VALUE) {
        return (
            <CopyToClipboard>
                <span>{value}</span>
            </CopyToClipboard>
        );
    }

    return (
        <CopyToClipboard>
            <span>n/a</span>
        </CopyToClipboard>
    );
};

const getCellRendererByType = (value: any, rowData: any, atrbType: string, columnName: any, navigate: any, additionalLinkColumns: any) => {
    // CHECK IF THE COLUMN IS SPECIFIED AS A LINK
    const additionalLinkColumn = additionalLinkColumns?.find((obj) => obj.columnName === columnName);
    if (additionalLinkColumn) {
        return <CopyToClipboard>{additionalLinkColumnRenderer(value, rowData, additionalLinkColumn, navigate)}</CopyToClipboard>;
    }
    if (atrbType === "date" || atrbType === "timestamp") {
        return renderLocalDateTime(value);
    }
    if (atrbType === "boolean") {
        return <CopyToClipboard>{booleanCellRenderer(value)}</CopyToClipboard>;
    }
    return defaultCellRenderer(value);
};

export const getFilter = (type) => {
    if (type === "text" || type === "json" || type === "lookup") {
        return "agTextColumnFilter";
    }
    if (type === "date" || type === "timestamp") {
        return "agDateColumnFilter";
    }
    if (type === "boolean" || type === "value list") {
        return "agSetColumnFilter";
    }
    return "agNumberColumnFilter";
};

const buildBooleanValueFormatter = (params: any) => {
    const { value } = params;
    // Intentional asymmetry: "Yes"/"No" are only emitted for a known boolean (real or the "true"/
    // "false" strings, via toBool for the truthy side); anything unknown (undefined, "", other) falls
    // through to "" so an unset cell renders blank rather than a misleading "No". Do not collapse the
    // "No" branch into a bare `else` — that would relabel missing data as a definite "No".
    if (toBool(value)) return "Yes";
    if (value === "false" || value === null || value === false) return "No";
    return "";
};

const buildBooleanFilterParams = (dataFetchingType: any, restApiDataClientSide?: boolean) => {
    if (dataFetchingType === "restApi" && !restApiDataClientSide) return { values: ["Yes", "No"] };
    if (dataFetchingType === "localData") return { valueFormatter: buildBooleanValueFormatter };
    return {};
};

const buildFilterParams = (attr: any, dataFetchingType: any, restApiDataClientSide: boolean | undefined, customColumnOptions: any) => ({
    ...(attr?.atrbType === "boolean" ? buildBooleanFilterParams(dataFetchingType, restApiDataClientSide) : {}),
    ...(attr?.atrbType === "value list" && {
        values: attr?.attributeValues?.map((attribute: any) => attribute?.name).filter((name: string) => name !== BO_CORE_DEFAULT_VALUE),
    }),
    ...((attr?.atrbType === "date" || attr?.atrbType === "timestamp") && {
        filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
    }),
    ...(customColumnOptions?.filterParams || {}),
});

export const generatingGridColumns = (
    dataInterface: any,
    showIdColumn: { show: boolean; sort?: "asc" | "desc" },
    initialShownColumns: any,
    navigate: any,
    editLink,
    editLinkNewTab,
    editLinkQueryParams,
    additionalLinkColumns: any,
    dataFetchingType: any,
    hasMasterRow: boolean,
    // customCellRenderers?: any,
    columnOptions?: any,
    restApiDataClientSide?: boolean,
    // rowDragging?: boolean,
    // disableRowSelecting?: boolean,
    disableFullTextSearch?: boolean,
    disableOptionMenu?: boolean,
) => {
    const records: any = [];
    let gridConvertedAttributes: any = dataInterface?.reduce((attrArray, attr) => {
        if (attr?.attrKey?.toLowerCase() === "id") {
            return attrArray;
        }
        records.push(attr?.attrKey);
        // let userDefinedCellRenderer;
        // if (customCellRenderers && customCellRenderers.length > 0) {
        //     userDefinedCellRenderer = customCellRenderers.find((cr) => cr.key === attr?.attrKey);
        // }

        let customColumnOptions;
        if (columnOptions && columnOptions.length > 0) {
            customColumnOptions = columnOptions?.find((col) => col?.key === attr.attrKey);
        }
        attrArray.push({
            field: attr?.attrKey,
            headerName: attr?.name,
            cellRenderer: /* userDefinedCellRenderer
            ? (value, params) => userDefinedCellRenderer.cellRendered(value, params.data)
            : */ (value, params) => getCellRendererByType(value, params.data, attr?.atrbType, attr?.attrKey, navigate, additionalLinkColumns),
            filter: getFilter(attr?.atrbType),
            filterParams: buildFilterParams(attr, dataFetchingType, restApiDataClientSide, customColumnOptions),
            floatingFilter: true,
            minWidth: 120,
            // resizable: true,
            hide: initialShownColumns ? !initialShownColumns?.includes(attr?.attrKey) : false,
            headerComponentParams: {
                innerHeaderComponent: ColumnHeaderCopyWrapper,
            },
            ...customColumnOptions?.options,
        });
        return attrArray;
    }, []);
    if (initialShownColumns) {
        const hiddenAttributes = gridConvertedAttributes?.filter((attribute: any) => attribute?.hide === true);
        const sortedGridAttributes = initialShownColumns?.map((attribute: any) => {
            return gridConvertedAttributes?.find((attr: any) => attr?.field === attribute);
        });
        gridConvertedAttributes = [...sortedGridAttributes, ...hiddenAttributes];
    }
    if (showIdColumn?.show && dataInterface?.some((attr: any) => attr?.attrKey?.toLowerCase() === "id")) {
        let customColumnOptions;
        if (columnOptions && columnOptions.length > 0) {
            customColumnOptions = columnOptions?.find((col) => col?.key === "id");
        }
        gridConvertedAttributes.unshift({
            field: "id",
            headerName: "ID",
            cellRenderer: (value, params) => idColumnCellRenderer(value, params, editLinkNewTab, editLink, editLinkQueryParams, navigate),
            filter: "agNumberColumnFilter",
            floatingFilter: true,
            minWidth: 120,
            hide: false,
            headerComponentParams: {
                innerHeaderComponent: ColumnHeaderCopyWrapper,
            },
            // resizable: true,
            ...(showIdColumn?.sort && { sort: showIdColumn?.sort }),
            ...customColumnOptions?.options,
        });
    }
    if (hasMasterRow) {
        gridConvertedAttributes.unshift({
            field: DEFAULT_COLID_AT,
            headerName: "",
            sortable: false,
            maxWidth: 32,
            suppressColumnsToolPanel: true,
            lockPinned: true,
            pinned: "left",
            lockPosition: "left",
            cellRenderer: "agGroupCellRenderer",
            floatingFilter: false,
            hide: false,
            resizable: false,
            cellStyle: {
                paddingLeft: "6px",
                paddingRight: "6px",
            },
        } as ColDef);
    }
    if (!disableOptionMenu) {
        gridConvertedAttributes.unshift({
            field: DEFAULT_COLID_DOTS,
            headerName: "",
            sortable: false,
            maxWidth: 40,
            suppressColumnsToolPanel: true,
            lockPinned: true,
            pinned: "left",
            lockPosition: "left",
            cellRenderer: (value, params) => renderDots(value, params),
            floatingFilter: false,
            hide: false,
            resizable: false,
            cellStyle: {
                padding: "5px",
            },
        } as ColDef);
    }
    if (!restApiDataClientSide && !disableFullTextSearch && dataFetchingType === "restApi") {
        gridConvertedAttributes.push({
            field: "restApiFts",
            hide: true,
            lockVisible: true,
            filter: "agTextColumnFilter",
            filterParams: {
                newRowsAction: "keep",
            },
            suppressColumnsToolPanel: true,
        });
    }
    return { gridConvertedAttributes, records };
};

export const renderUserDOBTooltip = (rowData: any) => {
    const tooltipTextList: string[] = [];

    if (rowData?.email) {
        tooltipTextList.push(`Email: ${rowData.email}`);
    }

    if (rowData?.birthDate) {
        tooltipTextList.push(`DOB: ${utcToLocalDateTime(rowData.birthDate, false)}`);
    }

    if (rowData?.phoneNumber) {
        tooltipTextList.push(`Phone Number: ${rowData.phoneNumber}`);
    }

    if (rowData?.id) {
        tooltipTextList.push(`1health ID: ${rowData.id}`);
    }

    const tooltipText = tooltipTextList.join(" • ");

    return rowData !== BO_CORE_DEFAULT_VALUE && rowData?.name ? (
        <>
            <Tooltip title={tooltipText}>
                <IconButton aria-label="View patient information">
                    <Info />
                </IconButton>
            </Tooltip>
            <span>{rowData?.name}</span>
        </>
    ) : (
        <span style={{ margin: "1rem" }}>
            <FormattedMessage id="common.label.no-patient" defaultMessage="No Patient" />
        </span>
    );
};

export const downloadRequisitionPDF = (id: number) => {
    getRequisitionPDF(id).then((res) => {
        if (res?.data) {
            const file = new Blob([res.data], { type: "application/pdf" });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
        }
    });
};

export const processFilterParams = (
    filterString: string,
    queryStrings: string[],
    replaceEmptyOperation?: [string, any],
    removeTicks = true,
    separator = " and ",
): Record<string, number | string> => {
    const output = {};
    if (filterString === "") {
        return output;
    }

    if (queryStrings.length === 0) {
        return output;
    }

    const regex = /(^'|'$)/g;

    const items = filterString
        .split(separator)
        .map((section: string) => {
            /* eslint-disable-next-line */
            for (const queryString of queryStrings) {
                if (section.includes(queryString)) {
                    /* eslint-disable-next-line */
                    let [key, val] = section.trim().split(queryString);

                    if (replaceEmptyOperation?.[0] === queryString && val === "''") {
                        val = `'${replaceEmptyOperation[1]}'`;
                    }
                    return removeTicks ? { [key]: val.replace(regex, "") } : { [key]: val };
                }
            }
            return undefined;
        })
        .filter((e) => e !== undefined);

    Object.assign(output, ...items);

    return output;
};

export const standardParamsSerializer = (params) => {
    const strings = Object.keys(params).map((key) => {
        if (!Array.isArray(params[key]) && params[key] !== null && params[key] !== undefined) {
            return `${key}=${encodeURIComponent(params[key])}`;
        }

        if (Array.isArray(params[key]) && params[key]?.length) {
            return params[key].map((e) => `${key}=${encodeURIComponent(e)}`).join("&");
        }

        return undefined;
    });

    return strings.filter((e) => e !== undefined).join("&");
};

export const isOnlySingleRowSelected = (gridApi: GridApi) => {
    const selectedRows = gridApi?.getCellRanges() || [];
    return selectedRows?.length === 1 && selectedRows?.[0].startRow?.rowIndex === selectedRows?.[0].endRow?.rowIndex;
};

export const shouldEnableDeactivateButton = (rData, rowData, deactivateButtonFunc, deactivateButtonActivationChecker) => {
    if (!deactivateButtonFunc) {
        return false;
    }

    if (typeof deactivateButtonActivationChecker === "function") {
        return deactivateButtonActivationChecker(rData);
    }
    if (typeof deactivateButtonActivationChecker === "string") {
        return rowData[deactivateButtonActivationChecker];
    }

    return true;
};

export const isValidContextOption = (params: GetContextMenuItemsParams) => {
    const isCustomContextMenuTrigger = params.column?.getColDef().field === DEFAULT_COLID_DOTS;
    const selectedRowsCount = getSelectedRowsCustom(params.api).length;
    return isCustomContextMenuTrigger || selectedRowsCount === 1;
};

export const viewPDFContextOption = (selectedRow, showOption = true) => {
    if (!showOption) {
        return [];
    }

    const normalizedSelectedRow = selectedRow.map((row) => ({
        ...row,
        testOrdered: row.testOfferedName || row.testOrdered || row?.lab,
    }));

    if (!normalizedSelectedRow?.[0]?.testOrdered) {
        return [];
    }

    return [
        {
            name: "Requisition PDF",
            action: () => {
                if (normalizedSelectedRow?.length === 1 && normalizedSelectedRow?.[0]?.id) {
                    downloadRequisitionPDF(normalizedSelectedRow?.[0]?.id);
                }
            },
            disabled: !(
                normalizedSelectedRow?.length === 1 &&
                normalizedSelectedRow?.[0]?.testOrdered &&
                normalizedSelectedRow?.[0]?.testOrdered !== BO_CORE_DEFAULT_VALUE &&
                showOption
            ),
        },
    ];
};

enum DownloadType {
    PDF = "PDF",
    ZIP = "ZIP",
}

const handleFileDownload = async (pdfType: DownloadType, orderId: number) => {
    try {
        switch (pdfType) {
            case DownloadType.PDF: {
                const response = await getTestResultPDF(orderId);
                const newFile = new File([response.data], `results-${orderId}.pdf`);
                saveAs(newFile);
                break;
            }
            case DownloadType.ZIP: {
                const response = await getZipArchiveForOrder(orderId);
                const newFile = new File([response.data], `results-${orderId}.zip`);
                saveAs(newFile);
                break;
            }
            default:
                break;
        }
    } catch (error: any) {
        if (!isHttpErrorMessageAvailable(error)) {
            toastError(error?.response?.data?.message ? error?.response?.data?.message : error?.message);
        }
    }
};

export const generateResultPDFOption = (selectedRows, showOption = true): MenuItemDef[] => {
    if (!selectedRows[0]?.resultPostedAt) {
        return [];
    }

    const orderId = selectedRows[0]?.id;

    if (!orderId || orderId === BO_CORE_DEFAULT_NUMBER_VALUE) {
        return [];
    }

    return [
        {
            name: "Generate Result PDF",
            action: () => handleFileDownload(DownloadType.PDF, Number(orderId)),
            disabled: !showOption,
        },
    ];
};

export const getAllZipFileOption = (selectedRows, showOption = true): MenuItemDef[] => {
    if (!selectedRows[0]?.resultPostedAt) {
        return [];
    }

    const orderId = selectedRows[0]?.id;

    if (!orderId || orderId === BO_CORE_DEFAULT_NUMBER_VALUE) {
        return [];
    }

    return [
        {
            name: "Download.zip(All files)",
            action: () => handleFileDownload(DownloadType.ZIP, Number(orderId)),
            disabled: !showOption,
        },
    ];
};
